import { Artist, ClientArtistSearch } from './ClientArtistSearch';
import { UserMusicDataService } from './UserMusicDataService';
import { ArtistPopularityService } from './ArtistPopularityService';
import { musicPlatformFactory } from './MusicPlatformFactory';
import { IMusicPlatformService, Artist as PlatformArtist } from '../types/MusicPlatform';
import { searchLogger } from '../utils/SearchLogger';
import { apiUrl } from './api';
import { logger } from '../utils/logger';

export interface SearchResult {
  artists: Artist[];
  source: 'client' | 'spotify' | 'hybrid';
  hasMore?: boolean;
}

export interface SetlistData {
  songs: Array<{
    name: string;
    frequency: number;
    playedIn: string;
    duration?: string;
    isEncore?: boolean;
    isCover?: boolean;
    coverArtist?: string;
    position?: number;
    appleMusicTrack?: {
      id: string;
      name: string;
      artistName: string;
      albumName: string;
      duration: number;
      artwork?: {
        url: string;
        width: number;
        height: number;
      };
      previewUrl?: string;
      playParams?: any;
      url: string;
    };
    found?: boolean;
  }>;
  context: string;
  confidence: string;
  setlistInfo?: {
    id: string;
    date: string;
    venue: string;
    city: string;
    state?: string;
    tour?: string;
  };
  artist?: {
    mbid: string;
    name: string;
    sortName: string;
    disambiguation?: string;
  };
  stats?: {
    totalSongs: number;
    foundOnAppleMusic: number;
    matchRate: number;
  };
}

interface CachedSearchResult {
  result: SearchResult;
  timestamp: number;
}

interface CachedSetlistData {
  data: SetlistData;
  timestamp: number;
}

export class OptimalArtistSearch {
  private clientSearch: ClientArtistSearch;
  private userDataService: UserMusicDataService;
  private popularityService: ArtistPopularityService;
  private searchCache = new Map<string, CachedSearchResult>();
  private setlistCache = new Map<string, CachedSetlistData>();
  private artistSetlistCache = new Map<string, { result: Artist | null; timestamp: number }>();
  private currentSearchController: AbortController | null = null;
  private debouncedSaveCache: () => void;
  
  // Enhanced caching settings
  private readonly SEARCH_CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours for search results (Spotify compliant)
  private readonly SETLIST_CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours for setlist data (static concert data)
  private readonly RECENTLY_PLAYED_CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours for recently played (refreshes when user returns)
  private readonly MAX_CACHE_SIZE = 100; // Limit cache size
  private readonly PERSISTENT_CACHE_KEY = 'recently_played_cache_v1';
  private readonly SEARCH_CACHE_KEY = 'search_results_cache_v1';
  private readonly SETLIST_CACHE_KEY = 'setlist_data_cache_v1';

  constructor() {
    this.clientSearch = new ClientArtistSearch();
    this.userDataService = UserMusicDataService.getInstance();
    this.popularityService = ArtistPopularityService.getInstance();
    
    // Initialize debounced cache save (save after 2 seconds of inactivity)
    let saveTimeout: NodeJS.Timeout;
    this.debouncedSaveCache = () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        this.savePersistentCache();
      }, 2000);
    };
    
    // Load persistent cache on initialization
    this.loadPersistentCache();
    
  }

  /**
   * Get the current music platform service (Spotify, Apple Music, etc.)
   */
  private getCurrentMusicPlatformService(): IMusicPlatformService | null {
    try {
      return musicPlatformFactory.getCurrentService();
    } catch (error) {
      logger.log('🎵 No music platform service available, using legacy Apple Music search');
      return null;
    }
  }

  /**
   * Convert platform Artist to ClientArtistSearch Artist
   */
  private convertPlatformArtistToClientArtist(platformArtist: PlatformArtist): Artist {
    return {
      mbid: platformArtist.mbid || '',
      name: platformArtist.name,
      sortName: platformArtist.sortName || platformArtist.name,
      verified: platformArtist.verified,
      source: platformArtist.source as any,
      artwork: platformArtist.artwork
    };
  }

  /**
   * Get suggested artists from pre-computed database (ultra-fast)
   * @param artistNames - User's recently played artist names
   * @param limit - Maximum number of artists to return
   * @returns Promise<Artist[]> - Artists with pre-validated setlist data
   */
  private async getSuggestedArtistsFromDatabase(artistNames: string[], limit: number = 5): Promise<Artist[]> {
    try {
      logger.log(`🏃‍♂️ Database lookup for ${artistNames.length} artists (target: ${limit} results)`);
      const startTime = performance.now();
      
      // Build query string with artist names
      const artistQuery = artistNames.slice(0, 15).join(','); // Limit to prevent URL overflow
      const requestUrl = `${apiUrl}/api/artists/suggested-recent?artists=${encodeURIComponent(artistQuery)}&limit=${limit}`;
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Database API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const fetchTime = performance.now() - startTime;
      
      if (!data.artists || !Array.isArray(data.artists)) {
        throw new Error('Invalid response format from database API');
      }

      logger.log(`⚡ Database returned ${data.artists.length} artists in ${fetchTime.toFixed(0)}ms`);
      
      // Convert database format to our Artist interface
      const artists: Artist[] = data.artists.map((dbArtist: any) => ({
        mbid: dbArtist.mbid,
        name: dbArtist.name,
        sortName: dbArtist.sortName || dbArtist.name,
        verified: dbArtist.verified || true,
        source: 'database' as any,
        artwork: null, // Will be populated by platform search if needed
        popularityScore: dbArtist.popularityScore,
        setlistStatus: dbArtist.setlistStatus
      }));

      // Enhance with platform data for artwork (in parallel, non-blocking)
      this.enhanceArtistsWithPlatformDataAsync(artists);

      return artists;
      
    } catch (error) {
      logger.warn('❌ Database lookup failed:', error);
      throw error;
    }
  }

  /**
   * Enhance artists with platform data asynchronously (non-blocking)
   * This adds artwork and platform-specific data without slowing down the initial response
   */
  private async enhanceArtistsWithPlatformDataAsync(artists: Artist[]): Promise<void> {
    try {
      logger.log(`🎨 Enhancing ${artists.length} database artists with platform artwork...`);
      
      // Process artwork enhancement in background
      setTimeout(async () => {
        for (const artist of artists) {
          try {
            const platformResults = await this.searchCurrentPlatform(artist.name);
            if (platformResults.length > 0 && platformResults[0].artwork) {
              artist.artwork = platformResults[0].artwork;
              logger.log(`🎨 Added artwork for ${artist.name}`);
            }
          } catch (error) {
            // Silently fail - artwork is not critical
            logger.log(`⚠️ Could not get artwork for ${artist.name}`);
          }
        }
      }, 100); // Small delay to not block main response
      
    } catch (error) {
      logger.warn('⚠️ Platform enhancement failed:', error);
    }
  }

  /**
   * Enhanced search for artists using current platform or fallback to Apple Music
   */
  private async searchCurrentPlatform(query: string): Promise<Artist[]> {
    const platformService = this.getCurrentMusicPlatformService();
    
    logger.log(`🎵 searchCurrentPlatform called with query: "${query}"`);
    logger.log(`🎵 Platform service available:`, platformService ? `${platformService.platformType}` : 'None');
    
    if (platformService) {
      try {
        logger.log(`🎵 Searching ${platformService.platformType} for: "${query}"`);
        const platformArtists = await platformService.searchArtists(query);
        logger.log(`🎵 Platform search returned ${platformArtists.length} artists for "${query}"`);
        const convertedArtists = platformArtists.map(artist => this.convertPlatformArtistToClientArtist(artist));
        logger.log(`🎵 Converted to ${convertedArtists.length} client artists`);
        return convertedArtists;
      } catch (error) {
        logger.log(`🎵 ${platformService.platformType} search failed, falling back to Apple Music:`, error);
      }
    }
    
    // Fallback to legacy Apple Music search
    try {
      throw new Error('Apple Music search not supported');
    } catch (error) {
      logger.log('🎵 Apple Music search also failed:', error);
      return [];
    }
  }

  /**
   * Main search method that intelligently routes queries with MusicBrainz Live Data
   * @param query - Search query from user
   * @returns Promise<SearchResult> - Artists from appropriate source
   */
  async search(query: string): Promise<SearchResult> {
    // Handle empty query - return empty results (recently played removed)
    if (!query || query.length === 0) {
      return {
        artists: [],
        source: 'hybrid'
      };
    }

    const searchId = searchLogger.startSearch(query);
    const normalizedQuery = query.toLowerCase().trim();
    const cacheKey = `search_${normalizedQuery}`;

    try {
      // Check cache first
      const cachedResult = this.getCachedSearchResult(cacheKey);
      if (cachedResult) {
        searchLogger.cache(searchId, 'hit', `source: ${cachedResult.source}`);
        
        // Validate cached result
        searchLogger.validation(searchId, 'start');
        if (this.validateCachedResult(cachedResult, normalizedQuery)) {
          searchLogger.validation(searchId, 'pass');
          searchLogger.endSearch(searchId, cachedResult.artists.length, `cache-${cachedResult.source}`);
          return cachedResult;
        } else {
          searchLogger.validation(searchId, 'fail', { reason: 'Cached results not relevant to query' });
          this.searchCache.delete(cacheKey);
        }
      } else {
        searchLogger.cache(searchId, 'miss');
      }

    let result: SearchResult | undefined;

    // Cancel any existing search request
    if (this.currentSearchController) {
      this.currentSearchController.abort();
    }
    
    // Create new request controller for cancellation
    this.currentSearchController = new AbortController();
    
    // Performance-first search: prioritize user's listening history
    try {
      result = await this.performanceFirstSearch(query, searchId);
      
      if (result && result.artists.length > 0) {
        // Performance logging is handled within performanceFirstSearch
      } else {
        throw new Error('No performance results found');
      }
    } catch (error) {
      searchLogger.platform(searchId, 'performance-first', 'fallback', { 
        reason: error instanceof Error ? error.message : 'Unknown error' 
      });
      
      // Fallback: Try platform-specific search (Spotify, Apple Music, etc.)
      const platformService = this.getCurrentMusicPlatformService();
      if (platformService) {
        try {
          searchLogger.platform(searchId, platformService.platformType, 'start');
          const platformStart = performance.now();
          const platformResults = await this.searchCurrentPlatform(query);
          const platformTime = performance.now() - platformStart;
          
          if (platformResults.length > 0) {
            searchLogger.platform(searchId, platformService.platformType, 'success', { 
              count: platformResults.length, 
              duration: platformTime 
            });
            result = {
              artists: platformResults,
              source: 'hybrid',
              hasMore: platformResults.length >= 8
            };
          } else {
            throw new Error('No platform results found');
          }
        } catch (platformError) {
          searchLogger.platform(searchId, platformService.platformType, 'fallback', { 
            reason: platformError instanceof Error ? platformError.message : 'Unknown error' 
          });
        }
      }
    }
    
    // Use filtered backend endpoint if no platform service or platform search failed
    if (!result) {
      try {
        logger.log(`🎵 Filtered backend search for: "${query}"`);
        const apiUrl = process.env.REACT_APP_API_URL || 
          (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://setlist-playlist-generator.onrender.com');
        const response = await fetch(`${apiUrl}/api/artists/search?q=${encodeURIComponent(query)}`, {
          signal: this.currentSearchController.signal
        });
        
        if (!response.ok) {
          throw new Error(`Backend search failed: ${response.status}`);
        }
        
        const data = await response.json();
        const backendResults = (data.artists || []).map((artist: any) => ({
          mbid: artist.mbid,
          name: artist.name,
          sortName: artist.sortName,
          disambiguation: artist.disambiguation || '',
          verified: true,
          source: 'musicbrainz' as const,
          searchScore: artist.score || 100
        }));
        
        result = {
          artists: backendResults,
          source: 'hybrid',
          hasMore: backendResults.length >= 8
        };
        
        logger.log(`✅ Filtered backend returned ${backendResults.length} results (all with setlist data)`);
        
        // If we have an exact match, prioritize it immediately
        const exactMatch = backendResults.find((artist: any) => 
          artist.name.toLowerCase() === query.toLowerCase()
        );
        if (exactMatch) {
          logger.log(`🎯 Exact match found: ${exactMatch.name} - prioritizing`);
          result.artists = [exactMatch, ...backendResults.filter((a: any) => a.mbid !== exactMatch.mbid)];
        }
        
      } catch (error) {
        // Handle request cancellation gracefully
        if (error instanceof Error && error.name === 'AbortError') {
          logger.log(`🚫 Search cancelled for: "${query}"`);
          return { artists: [], source: 'hybrid' };
        }
        
        logger.error(`❌ Filtered backend search failed for "${query}":`, error);
        // Fallback to user's recently played when search fails
        const recentlyPlayed = await this.getRecentlyPlayedWithArtwork();
        result = {
          artists: recentlyPlayed,
          source: 'hybrid'
        };
      }
    }

      // Ensure we have a result to return
      if (!result) {
        searchLogger.error(searchId, 'SEARCH', 'All search methods failed');
        result = {
          artists: [],
          source: 'hybrid'
        };
      }

      // Apply fast sorting for immediate results
      if (result.artists.length > 0) {
        // Use fast sorting first (no API calls) with query relevance
        result.artists = this.sortArtistsByFastRelevance(result.artists, query);
        searchLogger.cache(searchId, 'set', `${result.artists.length} results`);
        this.setCachedSearchResult(cacheKey, result);
      }

      searchLogger.endSearch(searchId, result.artists.length, result.source);
      return result;
      
    } catch (error) {
      searchLogger.error(searchId, 'SEARCH', 'Search operation failed', error);
      searchLogger.endSearch(searchId, 0, 'error');
      return {
        artists: [],
        source: 'hybrid'
      };
    }
  }

  /**
   * Fast sorting without API calls - returns results immediately
   * @param artists - Array of artists to sort
   * @param query - Original search query for relevance scoring
   * @returns Artist[] - Sorted artists (synchronous)
   */
  private sortArtistsByFastRelevance(artists: Artist[], query?: string): Artist[] {
    if (artists.length === 0) return artists;

    logger.log(`⚡ Fast sorting ${artists.length} artists (no API calls)`);
    
    return artists.sort((a, b) => {
      // Calculate query-specific relevance if we have a query
      let queryRelevanceA = 0;
      let queryRelevanceB = 0;
      
      if (query) {
        queryRelevanceA = this.calculateQueryRelevance(a.name, query);
        queryRelevanceB = this.calculateQueryRelevance(b.name, query);
      }
      
      // Use cached user data (fast lookup)
      const userScoreA = this.userDataService.hasUserData() ? 
        this.userDataService.calculateUserScoring(a).userRelevanceScore : 0;
      const userScoreB = this.userDataService.hasUserData() ? 
        this.userDataService.calculateUserScoring(b).userRelevanceScore : 0;
      
      // Use simple popularity from built-in database (fast lookup)
      const popularityA = this.getSimplePopularityScore(a.name);
      const popularityB = this.getSimplePopularityScore(b.name);
      
      // Use MusicBrainz search score (already available)
      const searchScoreA = a.searchScore || 0;
      const searchScoreB = b.searchScore || 0;
      
      // Calculate composite score with query relevance prioritized
      // Query relevance gets the highest weight to ensure relevant matches come first
      const compositeA = (queryRelevanceA * 0.5) + (userScoreA * 0.2) + (searchScoreA * 0.2) + (popularityA * 0.1);
      const compositeB = (queryRelevanceB * 0.5) + (userScoreB * 0.2) + (searchScoreB * 0.2) + (popularityB * 0.1);
      
      return compositeB - compositeA; // Descending order
    });
  }

  /**
   * Calculate how relevant an artist name is to the search query
   * @param artistName - Name of the artist
   * @param query - Search query
   * @returns number - Relevance score (0-100)
   */
  private calculateQueryRelevance(artistName: string, query: string): number {
    const normalizedArtist = artistName.toLowerCase().trim();
    const normalizedQuery = query.toLowerCase().trim();
    
    // Exact match gets highest score
    if (normalizedArtist === normalizedQuery) {
      return 100;
    }
    
    // Check if artist name starts with query (high relevance)
    if (normalizedArtist.startsWith(normalizedQuery)) {
      return 90;
    }
    
    // Check if query starts with artist name (also high relevance)
    if (normalizedQuery.startsWith(normalizedArtist)) {
      return 85;
    }
    
    // Check for word-level matches
    const artistWords = normalizedArtist.split(/\s+/);
    const queryWords = normalizedQuery.split(/\s+/);
    
    // Count exact word matches
    let exactWordMatches = 0;
    let partialWordMatches = 0;
    
    for (const queryWord of queryWords) {
      for (const artistWord of artistWords) {
        if (artistWord === queryWord) {
          exactWordMatches++;
        } else if (artistWord.includes(queryWord) || queryWord.includes(artistWord)) {
          partialWordMatches++;
        }
      }
    }
    
    // Calculate score based on word matches
    const totalQueryWords = queryWords.length;
    const exactMatchRatio = exactWordMatches / totalQueryWords;
    const partialMatchRatio = partialWordMatches / totalQueryWords;
    
    // All words match exactly
    if (exactMatchRatio === 1.0) {
      return 95;
    }
    
    // Most words match exactly
    if (exactMatchRatio >= 0.7) {
      return 80 + (exactMatchRatio * 10);
    }
    
    // Some exact matches plus partial matches
    if (exactMatchRatio > 0) {
      return 60 + (exactMatchRatio * 20) + (partialMatchRatio * 10);
    }
    
    // Only partial matches
    if (partialMatchRatio > 0) {
      return 30 + (partialMatchRatio * 20);
    }
    
    // Use string similarity as fallback
    const similarity = this.calculateSimilarity(normalizedArtist, normalizedQuery);
    return Math.max(0, similarity * 50); // Scale similarity to 0-50 range
  }

  /**
   * Background enhancement of search results (non-blocking)
   * @param cacheKey - Cache key for updating results
   * @param artists - Artists to enhance
   */
  private async enhanceResultsInBackground(cacheKey: string, artists: Artist[]): Promise<void> {
    try {
      logger.log(`🔄 Background enhancement starting for ${artists.length} artists`);
      
      // Do the full scoring in background
      const enhancedArtists = await this.sortArtistsByRelevance(artists);
      
      // Update cache with enhanced results
      const enhancedResult: SearchResult = {
        artists: enhancedArtists,
        source: 'hybrid',
        hasMore: enhancedArtists.length >= 8
      };
      
      this.setCachedSearchResult(cacheKey, enhancedResult);
      logger.log(`✅ Background enhancement completed for "${cacheKey}"`);
      
    } catch (error) {
      logger.warn('⚠️ Background enhancement failed:', error);
    }
  }

  /**
   * Sort artists by relevance using user data and popularity scores
   * @param artists - Array of artists to sort
   * @returns Promise<Artist[]> - Sorted artists
   */
  private async sortArtistsByRelevance(artists: Artist[]): Promise<Artist[]> {
    if (artists.length === 0) return artists;

    try {
      // Get popularity scores in parallel with user profile loading
      const popularityScores = await this.popularityService.getPopularityScores(artists);

      // Calculate composite scores for each artist
      const artistsWithScores = artists.map(artist => {
        const userScoring = this.userDataService.calculateUserScoring(artist);
        const popularityScore = popularityScores.get(artist.name) || 50; // Default to moderate popularity
        const originalScore = artist.searchScore || 0; // MusicBrainz relevance score

        // Two-tier scoring: User relevance (primary) + Popularity (secondary)
        const finalScore = this.calculateFinalScore(
          userScoring.userRelevanceScore,
          popularityScore,
          originalScore
        );

        return {
          ...artist,
          finalScore,
          userRelevanceScore: userScoring.userRelevanceScore,
          popularityScore,
          isPersonalized: userScoring.userRelevanceScore > 0
        };
      });

      // Sort by final score (descending)
      artistsWithScores.sort((a, b) => {
        // Primary sort: Final composite score
        if (b.finalScore !== a.finalScore) {
          return b.finalScore - a.finalScore;
        }
        
        // Secondary sort: Original search relevance
        const scoreA = a.searchScore || 0;
        const scoreB = b.searchScore || 0;
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        
        // Tertiary sort: Alphabetical by name
        return a.name.localeCompare(b.name);
      });

      logger.log('🎯 Artist relevance sorting applied:', artistsWithScores.slice(0, 3).map(a => ({
        name: a.name,
        finalScore: a.finalScore,
        userScore: a.userRelevanceScore,
        popularityScore: a.popularityScore,
        isPersonalized: a.isPersonalized
      })));

      return artistsWithScores;
    } catch (error) {
      logger.warn('⚠️ Failed to sort by relevance, using original order:', error);
      return artists;
    }
  }

  /**
   * Calculate final composite score combining user relevance, popularity, and search relevance
   * @param userScore - User relevance score (0-120)
   * @param popularityScore - Artist popularity score (0-100)
   * @param searchScore - Original search relevance score (0-100)
   * @returns Final composite score
   */
  private calculateFinalScore(userScore: number, popularityScore: number, searchScore: number): number {
    // If user has personalized data, prioritize it heavily
    if (userScore > 0) {
      // User relevance gets 70% weight, popularity gets 20%, search relevance gets 10%
      return Math.round(userScore * 0.7 + popularityScore * 0.2 + searchScore * 0.1);
    } else {
      // No user data: popularity gets 60% weight, search relevance gets 40%
      return Math.round(popularityScore * 0.6 + searchScore * 0.4);
    }
  }

  /**
   * Get setlist data for a selected artist
   * @param artist - Selected artist from search results
   * @returns Promise<SetlistData> - Setlist information
   */
  async getArtistSetlists(artist: Artist): Promise<SetlistData> {
    try {
      let mbid = artist.mbid;

      // Use Spotify ID directly as MBID
      if (!mbid) {
        logger.log(`⚠️ No artist ID found for: ${artist.name}`);
        throw new Error('Artist ID required');
      }
      logger.log(`✅ Using artist ID: ${mbid}`);
      
      logger.log(`🎯 Final MBID for ${artist.name}: ${mbid}`);

      // Now generate cache key with the correct (mapped) MBID
      const cacheKey = `setlist_${mbid}`;
      const cachedSetlistData = this.getCachedSetlistData(cacheKey);
      if (cachedSetlistData) {
        logger.log(`💾 Using cached setlist data for: ${cacheKey}`);
        return cachedSetlistData;
      }

      logger.log(`🎵 Fetching recent setlist data from Setlist.fm for ${artist.name} (${mbid})`);

      // Call Setlist.fm API through backend proxy (fixes CORS issue)
      const backendUrl = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://setlist-playlist-generator.onrender.com');
      // IMPORTANT: Use artist name for the endpoint since mbid might be a Spotify ID
      // Encode the artist name properly for URL
      const encodedArtistName = encodeURIComponent(artist.name);
      const proxyUrl = `${backendUrl}/api/recent-setlist/${encodedArtistName}`;
      logger.log(`🌐 Making Setlist.fm API call via backend proxy: ${proxyUrl}`);
      logger.log(`🔍 Using endpoint: /api/recent-setlist/${encodedArtistName}`);
      
      const response = await fetch(proxyUrl, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const setlistResponse = await response.json();
        logger.log(`✅ Received setlist data from backend recent-setlist endpoint`);
        logger.log('📊 Setlist data structure:', setlistResponse);
        logger.log('🎤 Artist from response:', setlistResponse.artist);
        logger.log('🎵 Songs array:', setlistResponse.songs);
        logger.log('📈 Songs count:', setlistResponse.songs ? setlistResponse.songs.length : 0);
        
        // Handle the API response format from /api/recent-setlist/:mbid
        const setlist = setlistResponse.setlist;
        const allSongs: Array<{name: string, isEncore: boolean, position: number, setlistDate: string}> = [];
        
        if (setlist && setlist.songs) {
          // Process songs from the recent setlist
          setlist.songs.forEach((song: any, index: number) => {
            allSongs.push({
              name: song.name,
              isEncore: song.isEncore || false,
              position: index + 1,
              setlistDate: setlist.date || 'recent'
            });
          });
        }
        
        // Create final song list using the setlist format
        const songs = setlist && setlist.songs ? setlist.songs.map((song: any, index: number) => ({
          name: song.name,
          frequency: 100, // Recent setlist, so 100%
          playedIn: '1/1 shows',
          duration: '3:30',
          isEncore: song.isEncore || false,
          isCover: song.isCover || false,
          coverArtist: song.coverArtist,
          position: index + 1,
          found: true
        })) : [];
        
        logger.log(`🎵 Processed ${songs.length} songs from average setlist data`);
        logger.log('🎵 First 3 songs:', songs.slice(0, 3));
        
        const finalSetlistData: SetlistData = {
          songs,
          context: setlistResponse.context || `Average setlist based on setlist.fm data`,
          confidence: setlistResponse.metadata?.confidence || (songs.length > 5 ? 'high' : 'medium'),
          setlistInfo: setlistResponse.setlistInfo,
          artist: {
            name: artist.name,
            mbid: mbid,
            sortName: artist.sortName || artist.name
          },
          stats: setlistResponse.stats
        };
        
        logger.log(`🎶 Processed ${songs.length} songs from setlist data`);
        logger.log('🎵 Top 3 songs:', songs.slice(0, 3).map((s: any) => `${s.name} (${s.frequency}%)`));
        
        // Cache the result
        this.setCachedSetlistData(cacheKey, finalSetlistData);
        
        return finalSetlistData;
      } else {
        const errorText = await response.text();
        logger.error(`⚠️ Recent setlist API failed (${response.status}): ${errorText}`);
        throw new Error(`API failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      logger.warn('⚠️ Failed to fetch recent setlist:', error);
      // Only use mock data for artists we have actual mock data for
      const knownMockArtists = ['Taylor Swift', 'Arctic Monkeys', 'The Beatles', 'Led Zeppelin', 'Radiohead', 'Nirvana', 'The Rolling Stones', 'Pixies'];
      
      if (knownMockArtists.includes(artist.name)) {
        logger.log('🎭 Using mock data for known artist:', artist.name);
        const mockData = this.getMockSetlistData(artist.name);
        return mockData;
      }
      
      // For other artists, throw the error so the UI can display a proper message
      logger.error('❌ No setlist data available for:', artist.name);
      throw new Error(`Unable to load setlist data for ${artist.name}. Please try again later.`);
    }
  }

  /**
   * Enhanced search that merges client and Apple Music results
   * @param query - Search query
   * @returns Promise<SearchResult> - Combined results
   */
  async hybridSearch(query: string): Promise<SearchResult> {
    if (!query || query.length < 3) {
      return this.search(query);
    }

    try {
      // Get results from both sources
      const [clientResults, platformResults] = await Promise.all([
        Promise.resolve(this.clientSearch.search(query)),
        this.searchCurrentPlatform(query)
      ]);

      // Merge and deduplicate results
      const mergedResults = this.mergeResults(clientResults, platformResults);

      // Apply intelligent sorting
      const sortedResults = await this.sortArtistsByRelevance(mergedResults);

      const result: SearchResult = {
        artists: sortedResults,
        source: 'hybrid',
        hasMore: platformResults.length >= 8
      };

      // Cache the result
      this.setCachedSearchResult(query.toLowerCase().trim(), result);

      return result;
    } catch (error) {
      return this.search(query);
    }
  }

  /**
   * Get recently played artists with hybrid approach: fast initial + background validation
   * Returns immediately with basic artists, then validates in background
   */
  async getRecentlyPlayedWithArtworkHybrid(
    onInitialResults?: (artists: Artist[]) => void,
    onValidatedResults?: (artists: Artist[]) => void
  ): Promise<Artist[]> {
    try {
      logger.log('🚀 Hybrid mode: Getting immediate suggestions from database...');
      
      // Phase 1: Get immediate results from database (ultra-fast)
      const immediateResults = await this.getRecentlyPlayedWithArtworkFast();
      logger.log(`⚡ Phase 1: Returning ${immediateResults.length} immediate suggestions`);
      
      // Call immediate callback if provided
      if (onInitialResults) {
        onInitialResults(immediateResults);
      }
      
      // Phase 2: If we got database results, they're already validated - no background processing needed
      if (immediateResults.length > 0 && immediateResults[0].source === 'database') {
        logger.log('✅ Database results are pre-validated, skipping background validation');
        if (onValidatedResults) {
          onValidatedResults(immediateResults);
        }
      } else {
        // Phase 2: Start background validation only for non-database results
        logger.log('🔄 Non-database results, starting background validation...');
        this.validateArtistsInBackground(immediateResults, onValidatedResults);
      }
      
      // Return immediate results for compatibility
      return immediateResults;
      
    } catch (error) {
      logger.warn('⚠️ Hybrid mode error:', error);
      return [];
    }
  }

  /**
   * Validate artists in background and return only those with setlist data
   */
  private async validateArtistsInBackground(
    initialArtists: Artist[],
    onValidatedResults?: (artists: Artist[]) => void
  ): Promise<void> {
    try {
      logger.log('🔄 Phase 2: Starting background validation for setlist data...');
      const startTime = performance.now();
      
      // Use the existing validation logic but run it in background
      const userProfile = await this.userDataService.getUserMusicProfile();
      
      if (this.userDataService.hasRecentlyPlayedData()) {
        const recentArtistNames = Array.from(userProfile.recentlyPlayedArtists);
        
        // Prioritize artists and validate (same as original method)
        const prioritizedArtists = await this.prioritizeArtistsByPopularity(recentArtistNames);
        const limitedArtists = prioritizedArtists.slice(0, 15);
        
        logger.log(`🔍 Validating ${limitedArtists.length} artists for setlist data...`);
        const results = await this.processArtistsInParallel(limitedArtists, 6);
        
        const validatedArtists: Artist[] = [];
        for (const result of results) {
          if (result && result.artwork) {
            validatedArtists.push(result);
          }
        }
        
        const validationTime = performance.now() - startTime;
        logger.log(`✅ Phase 2: Found ${validatedArtists.length} validated artists in ${validationTime.toFixed(0)}ms`);
        
        // If we have enough validated artists, use them
        if (validatedArtists.length >= 3) {
          if (onValidatedResults) {
            onValidatedResults(validatedArtists.slice(0, 5));
          }
          return;
        }
      }
      
      // Fallback: If validation didn't find enough artists, supplement with known good ones
      logger.log('⚠️ Background validation found <3 artists, supplementing with fallback');
      const fallbackArtists = await this.getFallbackPopularArtists();
      
      if (onValidatedResults) {
        onValidatedResults(fallbackArtists.slice(0, 5));
      }
      
    } catch (error) {
      logger.warn('⚠️ Background validation failed:', error);
      // Don't call callback on error - keep initial results
    }
  }

  /**
   * Get recently played artists with basic info immediately (fast version)
   * Skips expensive setlist validation for immediate response
   */
  async getRecentlyPlayedWithArtworkFast(): Promise<Artist[]> {
    try {
      logger.log('🚀 Fast mode: Getting recently played artists from pre-computed database...');
      
      // Get user's actual recently played artists (already optimized with batch calls)
      const userProfile = await this.userDataService.getUserMusicProfile();
      
      if (this.userDataService.hasRecentlyPlayedData()) {
        const recentArtistNames = Array.from(userProfile.recentlyPlayedArtists);
        logger.log(`⚡ Fast database lookup for ${recentArtistNames.length} recently played artists...`);

        // Try the new database endpoint first
        try {
          const databaseResults = await this.getSuggestedArtistsFromDatabase(recentArtistNames, 5);
          if (databaseResults.length > 0) {
            logger.log(`✅ Database mode: returning ${databaseResults.length} artists instantly`);
            return databaseResults;
          }
        } catch (dbError) {
          logger.warn('⚠️ Database lookup failed, falling back to platform search:', dbError);
        }

        // No fallback to platform search - only return pre-validated database results
        logger.log('⚠️ Database unavailable, no pre-validated artists available');
        return [];
      }
    } catch (error) {
      logger.warn('⚠️ Fast mode error, using fallback:', error);
    }

    // No fallback - only return pre-validated database results
    logger.log('📱 Fast mode: no database connection, returning empty results');
    return [];
  }

  /**
   * Get recently played artists with sufficient 6-month setlist.fm data only (parallel processing)
   * Uses same validation as search results to ensure consistency
   */
  async getRecentlyPlayedWithArtwork(): Promise<Artist[]> {
    try {
      // Get user's actual recently played artists
      const userProfile = await this.userDataService.getUserMusicProfile();
      
      if (this.userDataService.hasRecentlyPlayedData()) {
        const recentArtistNames = Array.from(userProfile.recentlyPlayedArtists);
        logger.log(`🚀 Processing ${recentArtistNames.length} recently played artists for setlist validation...`);
        logger.log(`📋 Artists to process: ${recentArtistNames.join(', ')}`);

        // Prioritize artists by popularity/likelihood to have setlist data
        const prioritizedArtists = await this.prioritizeArtistsByPopularity(recentArtistNames);
        logger.log(`📊 Prioritized order: ${prioritizedArtists.join(', ')}`);
        
        // Process up to 15 artists in parallel (increased for better results)
        const limitedArtists = prioritizedArtists.slice(0, 15);
        const artistsWithSetlists: Artist[] = [];
        
        // Parallel processing with increased concurrency control (max 6 concurrent requests)
        logger.log(`🔄 Processing ${limitedArtists.length} artists in parallel...`);
        const parallelStart = performance.now();
        const results = await this.processArtistsInParallel(limitedArtists, 6);
        const parallelTime = performance.now() - parallelStart;
        logger.log(`⚡ Parallel processing completed in ${parallelTime.toFixed(0)}ms (avg: ${(parallelTime / limitedArtists.length).toFixed(0)}ms per artist)`);
        
        // Extract successful results and stop at 5
        let processed = 0;
        for (const artist of results) {
          processed++;
          if (artist) {
            artistsWithSetlists.push(artist);
            logger.log(`✅ Added ${artist.name} to recently played with setlist data (${artistsWithSetlists.length}/5)`);
            if (artistsWithSetlists.length >= 5) break; // Early termination at 5 results
          } else {
            logger.log(`❌ Artist ${processed} failed validation or not found`);
          }
        }

        logger.log(`🎯 Found ${artistsWithSetlists.length} recently played artists with sufficient 6-month setlist data`);
        
        // If we have enough artists with recent data, use them
        if (artistsWithSetlists.length >= 3) {
          logger.log(`🎯 Strict validation complete: ${artistsWithSetlists.length} artists with confirmed 6-month setlist data`);
          return artistsWithSetlists;
        } else {
          logger.log(`⚠️ Only ${artistsWithSetlists.length} artists with recent data, supplementing with popular fallback artists`);
          // Supplement with fallback artists to ensure minimum variety
          const fallbackArtists = await this.getFallbackPopularArtists();
          const combined = [...artistsWithSetlists, ...fallbackArtists];
          
          // Deduplicate combined results by MBID and name
          const deduplicatedCombined = combined.filter((artist, index, self) => {
            return index === self.findIndex(a => 
              a.mbid === artist.mbid || a.name.toLowerCase() === artist.name.toLowerCase()
            );
          });
          
          return deduplicatedCombined.slice(0, 5);
        }
      }
    } catch (error) {
      logger.warn('⚠️ Could not fetch user recently played data:', error);
    }

    // Fallback to popular artists that are known to have setlist data
    logger.log('📱 No user data available, using popular artists fallback');
    return await this.getFallbackPopularArtists();
  }

  /**
   * Get fallback popular artists when no user data is available
   * These artists are known to have sufficient 6-month setlist data
   * Enhanced with real Apple Music artwork
   */
  private async getFallbackPopularArtists(): Promise<Artist[]> {
    logger.log('🎯 Providing fallback popular artists with recent setlist data (past 6 months)');
    
    // Base artists that we know have recent 6-month setlist data
    const baseArtists: Artist[] = [
      {
        mbid: '63aa26c3-d59b-4da4-84ac-716b54f1ef4d',
        name: 'Tame Impala',
        sortName: 'Tame Impala',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'af37c51c-0790-4a29-b995-456f98a6b8c9',
        name: 'Vampire Weekend',
        sortName: 'Vampire Weekend',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'aea4c9b9-9f8d-49dc-b2ca-57d6f26e8634',
        name: 'Khruangbin',
        sortName: 'Khruangbin',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'f181961b-20f7-459e-89de-920ef03c7ed0',
        name: 'The Strokes',
        sortName: 'Strokes, The',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'ada7a83c-e3e1-40f1-93f9-3e73dbc9298a',
        name: 'Arctic Monkeys',
        sortName: 'Arctic Monkeys',
        verified: true,
        source: 'fallback'
      }
    ];
    
    logger.log('🎨 Enhancing fallback artists with real Apple Music artwork...');
    
    // Enhance with real Apple Music artwork
    const enhancedArtists = await Promise.all(
      baseArtists.map(async (artist) => {
        try {
          logger.log(`🎨 Fetching artwork for ${artist.name}...`);
          const platformResults = await this.searchCurrentPlatform(artist.name);
          if (platformResults.length > 0 && platformResults[0].artwork) {
            const platformType = this.getCurrentMusicPlatformService()?.platformType || 'music platform';
            logger.log(`✅ Found ${platformType} artwork for ${artist.name}`);
            return {
              ...artist,
              artwork: platformResults[0].artwork
            };
          } else {
            logger.log(`⚠️ No artwork found for ${artist.name}`);
          }
        } catch (error) {
          logger.log(`❌ Could not get artwork for ${artist.name}:`, error);
        }
        return artist; // Return without artwork if enhancement fails
      })
    );
    
    // Deduplicate before returning to prevent duplicates
    const deduplicatedArtists = enhancedArtists.filter((artist, index, self) => {
      return index === self.findIndex(a => a.mbid === artist.mbid || a.name.toLowerCase() === artist.name.toLowerCase());
    });
    
    logger.log(`🎯 Returning ${deduplicatedArtists.length} fallback artists with Apple Music artwork`);
    return deduplicatedArtists;
  }

  /**
   * Get fallback popular artists immediately without API calls (fast version)
   */
  private getFallbackPopularArtistsFast(): Artist[] {
    logger.log('⚡ Fast mode: Returning popular artists immediately without API calls');
    
    // Return popular artists with basic info - no API calls needed
    const popularArtists: Artist[] = [
      {
        mbid: '63aa26c3-d59b-4da4-84ac-716b54f1ef4d',
        name: 'Tame Impala',
        sortName: 'Tame Impala',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'af37c51c-0790-4a29-b995-456f98a6b8c9',
        name: 'Vampire Weekend',
        sortName: 'Vampire Weekend',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'aea4c9b9-9f8d-49dc-b2ca-57d6f26e8634',
        name: 'Khruangbin',
        sortName: 'Khruangbin',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'f181961b-20f7-459e-89de-920ef03c7ed0',
        name: 'The Strokes',
        sortName: 'Strokes, The',
        verified: true,
        source: 'fallback'
      },
      {
        mbid: 'ada7a83c-e3e1-40f1-93f9-3e73dbc9298a',
        name: 'Arctic Monkeys',
        sortName: 'Arctic Monkeys',
        verified: true,
        source: 'fallback'
      }
    ];

    logger.log(`⚡ Fast mode: Returning ${popularArtists.length} popular artists immediately`);
    return popularArtists;
  }

  /**
   * Load persistent cache from localStorage
   */
  private loadPersistentCache(): void {
    try {
      // Load recently played cache
      const recentlyPlayedCache = localStorage.getItem(this.PERSISTENT_CACHE_KEY);
      if (recentlyPlayedCache) {
        const data = JSON.parse(recentlyPlayedCache);
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          if (value.timestamp && Date.now() - value.timestamp < this.RECENTLY_PLAYED_CACHE_DURATION) {
            // Only load cached artists that have artwork
            if (value.result && value.result.artwork) {
              this.artistSetlistCache.set(key, value);
            } else {
              logger.log(`🎨 Skipping cached artist ${value.result?.name || key} without artwork`);
            }
          }
        });
        logger.log(`📦 Loaded ${Object.keys(data).length} recently played items from persistent cache`);
      }

      // Load search results cache
      const searchCache = localStorage.getItem(this.SEARCH_CACHE_KEY);
      if (searchCache) {
        const data = JSON.parse(searchCache);
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          if (value.timestamp && Date.now() - value.timestamp < this.SEARCH_CACHE_DURATION) {
            this.searchCache.set(key, value as CachedSearchResult);
          }
        });
        logger.log(`🔍 Loaded ${Object.keys(data).length} search results from persistent cache`);
      }

      // Load setlist data cache
      const setlistCache = localStorage.getItem(this.SETLIST_CACHE_KEY);
      if (setlistCache) {
        const data = JSON.parse(setlistCache);
        Object.entries(data).forEach(([key, value]: [string, any]) => {
          if (value.timestamp && Date.now() - value.timestamp < this.SETLIST_CACHE_DURATION) {
            this.setlistCache.set(key, value as CachedSetlistData);
          }
        });
        logger.log(`🎵 Loaded ${Object.keys(data).length} setlist data from persistent cache`);
      }
    } catch (error) {
      logger.warn('⚠️ Failed to load persistent cache:', error);
    }
  }

  /**
   * Save cache to localStorage
   */
  private savePersistentCache(): void {
    try {
      // Save recently played cache
      const recentlyPlayedData: Record<string, any> = {};
      this.artistSetlistCache.forEach((value, key) => {
        if (key.startsWith('artist_setlist_') || key.startsWith('mb_artist_')) {
          recentlyPlayedData[key] = value;
        }
      });
      localStorage.setItem(this.PERSISTENT_CACHE_KEY, JSON.stringify(recentlyPlayedData));

      // Save search results cache (limit to most recent 20 searches)
      const searchData: Record<string, any> = {};
      const searchEntries = Array.from(this.searchCache.entries())
        .sort(([,a], [,b]) => b.timestamp - a.timestamp)
        .slice(0, 20);
      searchEntries.forEach(([key, value]) => {
        searchData[key] = value;
      });
      localStorage.setItem(this.SEARCH_CACHE_KEY, JSON.stringify(searchData));

      // Save setlist data cache (limit to most recent 30 setlists)
      const setlistData: Record<string, any> = {};
      const setlistEntries = Array.from(this.setlistCache.entries())
        .sort(([,a], [,b]) => b.timestamp - a.timestamp)
        .slice(0, 30);
      setlistEntries.forEach(([key, value]) => {
        setlistData[key] = value;
      });
      localStorage.setItem(this.SETLIST_CACHE_KEY, JSON.stringify(setlistData));
      
      logger.log(`💾 Saved persistent cache: ${Object.keys(recentlyPlayedData).length} recently played, ${searchEntries.length} searches, ${setlistEntries.length} setlists`);
    } catch (error) {
      logger.warn('⚠️ Failed to save persistent cache:', error);
    }
  }

  /**
   * Enhanced cache method with automatic persistence
   */
  private setInCachePersistent(key: string, value: any, duration: number): void {
    this.setInCache(key, value, duration);
    
    // Save to persistent storage if it's a recently played related cache
    if (key.startsWith('artist_setlist_') || key.startsWith('mb_artist_')) {
      this.savePersistentCache();
    }
  }

  /**
   * Warm the cache for recently played artists in the background (non-blocking)
   */
  async warmRecentlyPlayedCache(): Promise<void> {
    try {
      // Run in background without blocking
      setTimeout(async () => {
        try {
          const userProfile = await this.userDataService.getUserMusicProfile();
          if (this.userDataService.hasUserData()) {
            const recentArtistNames = Array.from(userProfile.recentlyPlayedArtists).slice(0, 5);
            logger.log(`🔄 Background warming cache for ${recentArtistNames.length} artists...`);
            
            // Process artists one by one to avoid overwhelming APIs
            for (const artistName of recentArtistNames) {
              try {
                // Check if already cached
                const cacheKey = `artist_setlist_${artistName.toLowerCase()}`;
                const cached = this.getFromCache(cacheKey);
                if (!cached) {
                  // Pre-cache basic artist data (without artwork to save bandwidth)
                  await this.getArtistData(artistName, false);
                  await new Promise(resolve => setTimeout(resolve, 500)); // Gentle delay
                }
              } catch (error) {
                // Silently continue on errors during background warming
              }
            }
            logger.log('✅ Background cache warming completed');
          }
        } catch (error) {
          logger.warn('⚠️ Background cache warming failed:', error);
        }
      }, 0);
    } catch (error) {
      // Silent fail for background operation
    }
  }

  /**
   * Prioritize artists by popularity/likelihood to have setlist data
   * @param artistNames - Array of artist names to prioritize
   * @returns Promise<string[]> - Prioritized array of artist names
   */
  private async prioritizeArtistsByPopularity(artistNames: string[]): Promise<string[]> {
    try {
      // Get popularity scores for all artists using a simpler approach
      const artistScores = artistNames.map((name) => {
        // Use the fallback popularity scoring from the popularity service
        const score = this.getSimplePopularityScore(name);
        return { name, score };
      });
      
      // Sort by popularity score (descending) - more popular artists first
      const sortedArtists = artistScores
        .sort((a, b) => b.score - a.score)
        .map(item => item.name);
      
      logger.log(`📊 Prioritized ${sortedArtists.length} artists by popularity`);
      return sortedArtists;
      
    } catch (error) {
      logger.warn('⚠️ Failed to prioritize artists, using original order:', error);
      return artistNames;
    }
  }

  /**
   * Get a simple popularity score for an artist name
   * @param artistName - Name of the artist
   * @returns number - Popularity score (0-100)
   */
  private getSimplePopularityScore(artistName: string): number {
    const normalizedName = artistName.toLowerCase().trim().replace(/^the\s+/i, '');
    
    // Built-in popularity database for major artists
    const popularityDatabase: { [key: string]: number } = {
      // Global superstars (90-100)
      'taylor swift': 100, 'drake': 98, 'the weeknd': 97, 'ariana grande': 96,
      'billie eilish': 95, 'dua lipa': 94, 'ed sheeran': 93, 'justin bieber': 92,
      'bad bunny': 91, 'beatles': 90,
      
      // Major artists (70-89)
      'radiohead': 85, 'coldplay': 84, 'arctic monkeys': 82, 'queen': 89,
      'pink floyd': 88, 'led zeppelin': 87, 'rolling stones': 86,
      'kendrick lamar': 83, 'kanye west': 81, 'beyoncé': 85,
      
      // Alternative/Indie favorites (60-79)
      'tame impala': 78, 'glass animals': 76, 'phoebe bridgers': 72,
      'vampire weekend': 74, 'strokes': 79, 'mac miller': 71,
      'frank ocean': 75, 'tyler the creator': 73, 'sza': 77,
      
      // Cult classics and influential artists (50-69)
      'stereolab': 68, 'pixies': 67, 'sonic youth': 65, 'my bloody valentine': 64,
      'kraftwerk': 66, 'aphex twin': 63, 'boards of canada': 62,
      'neutral milk hotel': 61, 'animal collective': 60, 'death grips': 59,
      
      // Legendary artists with enduring popularity (80-90)
      'david bowie': 89, 'prince': 88, 'nirvana': 87, 'bob dylan': 86,
      'johnny cash': 83, 'elvis presley': 85, 'michael jackson': 92
    };

    // Check popularity database
    if (popularityDatabase[normalizedName]) {
      return popularityDatabase[normalizedName];
    }

    // For unknown artists, return moderate score
    return 50;
  }

  /**
   * Process multiple artists in parallel with concurrency control
   * @param artistNames - Array of artist names to process
   * @param maxConcurrency - Maximum number of concurrent requests (default: 3)
   * @returns Promise<(Artist | null)[]> - Array of processed artists
   */
  private async processArtistsInParallel(artistNames: string[], maxConcurrency: number = 3): Promise<(Artist | null)[]> {
    const results: (Artist | null)[] = [];
    
    // Process in batches of maxConcurrency
    for (let i = 0; i < artistNames.length; i += maxConcurrency) {
      const batch = artistNames.slice(i, i + maxConcurrency);
      
      // Process batch in parallel
      const batchPromises = batch.map(artistName => 
        this.processArtistForSetlistData(artistName).catch(error => {
          logger.warn(`⚠️ Failed to process ${artistName}:`, error);
          return null;
        })
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Small delay between batches to be API-friendly
      if (i + maxConcurrency < artistNames.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    return results;
  }

  /**
   * Process a single artist to check for sufficient 6-month setlist data (optimized)
   * @param artistName - Name of the artist to process
   * @returns Promise<Artist | null> - Artist with sufficient 6-month setlist data or null
   */
  private async processArtistForSetlistData(artistName: string): Promise<Artist | null> {
    const startTime = performance.now();
    logger.log(`⏱️ Starting processing: ${artistName}`);
    
    try {
      // Check cache first, but skip cache if we need fresh artwork
      const cacheKey = `artist_setlist_${artistName.toLowerCase()}`;
      const cached = this.getFromCache(cacheKey);
      if (cached !== null && cached.artwork) { // Only use cache if it has artwork
        const cacheTime = performance.now() - startTime;
        logger.log(`💾 Cache hit for ${artistName} with artwork (${cacheTime.toFixed(0)}ms)`);
        return cached;
      } else if (cached !== null) {
        logger.log(`🔄 Cache hit for ${artistName} but missing artwork, refetching...`);
      }

      // First, get basic artist data without artwork (faster)
      const artistSearchStart = performance.now();
      const basicArtist = await Promise.race([
        this.getArtistData(artistName, false), // Skip artwork initially
        new Promise<Artist | null>((_, reject) => 
          setTimeout(() => reject(new Error('Artist search timeout')), 4000) // Reduced timeout
        )
      ]);
      const artistSearchTime = performance.now() - artistSearchStart;
      logger.log(`🔍 Artist search for ${artistName}: ${artistSearchTime.toFixed(0)}ms`);

      if (basicArtist && basicArtist.mbid) {
        logger.log(`🔍 Validating setlist data for ${basicArtist.name} (MBID: ${basicArtist.mbid})`);
        
        // Check if this artist has sufficient 6-month setlist data (same validation as search)
        const validationStart = performance.now();
        const hasSufficientSetlists = await this.hasSufficientSetlistData(basicArtist.mbid);
        const validationTime = performance.now() - validationStart;
        logger.log(`📊 Setlist validation for ${basicArtist.name}: ${validationTime.toFixed(0)}ms`);
        
        if (hasSufficientSetlists) {
          // Only now fetch the full artist data with artwork
          const artworkStart = performance.now();
          const fullArtist = await this.getArtistData(artistName, true);
          const artworkTime = performance.now() - artworkStart;
          logger.log(`🖼️ Artwork fetch for ${artistName}: ${artworkTime.toFixed(0)}ms`);
          
          const totalTime = performance.now() - startTime;
          logger.log(`✅ ${fullArtist?.name || artistName} completed successfully (${totalTime.toFixed(0)}ms total)`);
          this.setInCachePersistent(cacheKey, fullArtist, this.RECENTLY_PLAYED_CACHE_DURATION); // Cache for 1 hour
          return fullArtist;
        } else {
          const totalTime = performance.now() - startTime;
          logger.log(`❌ ${basicArtist.name} does not have sufficient 6-month setlist data (${totalTime.toFixed(0)}ms total)`);
          this.setInCachePersistent(cacheKey, null, 30 * 60 * 1000); // Cache negative result for 30 minutes
        }
      } else if (basicArtist) {
        const totalTime = performance.now() - startTime;
        logger.log(`⚠️ ${artistName} found but no MBID available (${totalTime.toFixed(0)}ms total)`);
      } else {
        const totalTime = performance.now() - startTime;
        logger.log(`❌ ${artistName} not found in MusicBrainz (${totalTime.toFixed(0)}ms total)`);
      }
      
      return null;
    } catch (error) {
      const totalTime = performance.now() - startTime;
      logger.warn(`⚠️ Could not process artist: ${artistName} (${totalTime.toFixed(0)}ms total)`, error);
      return null;
    }
  }

  /**
   * Get artist data with real MusicBrainz ID for setlist integration (optimized)
   * @param artistName - Name of the artist
   * @param fetchArtwork - Whether to fetch artwork (default: true)
   * @returns Promise<Artist | null> - Artist data with real MBID or null
   */
  private async getArtistData(artistName: string, fetchArtwork: boolean = true): Promise<Artist | null> {
    try {
      // Check cache first with a more specific key for artist data
      const cacheKey = `mb_artist_${artistName.toLowerCase()}`;
      const cached = this.getFromCache(cacheKey);
      if (cached !== null) {
        // If we need artwork but cached artist doesn't have it, skip cache
        if (fetchArtwork && !cached.artwork) {
          logger.log(`🎨 Cached artist ${cached.name} missing artwork, fetching fresh data`);
        } else {
          return cached;
        }
      }

      // Use MusicBrainz search to get real MBID for setlist data
      const mbResults = await this.searchMusicBrainzLive(artistName);
      if (mbResults.length > 0) {
        const mbArtist = mbResults[0];
        
        // Only fetch artwork if requested (to save API calls when we just need MBID)
        if (fetchArtwork) {
          try {
            const platformResults = await this.searchCurrentPlatform(artistName);
            if (platformResults.length > 0 && platformResults[0].artwork) {
              // Merge MusicBrainz MBID with platform artwork
              const artistWithArtwork = {
                ...mbArtist,
                artwork: platformResults[0].artwork
              };
              this.setInCachePersistent(cacheKey, artistWithArtwork, this.RECENTLY_PLAYED_CACHE_DURATION);
              return artistWithArtwork;
            }
          } catch (artworkError) {
            logger.log(`Could not get artwork for ${artistName}, using MusicBrainz data only`);
          }
        }
        
        this.setInCachePersistent(cacheKey, mbArtist, this.RECENTLY_PLAYED_CACHE_DURATION);
        return mbArtist;
      }
      
      this.setInCachePersistent(cacheKey, null, 30 * 60 * 1000); // Cache null results for 30 minutes
      return null;
    } catch (error) {
      logger.warn(`Failed to get artist data for ${artistName}:`, error);
      return null;
    }
  }

  /**
   * Check if an artist has sufficient 6-month setlist.fm data (same validation as search)
   * @param artistMbid - MusicBrainz ID of the artist
   * @returns Promise<boolean> - True if artist has sufficient 6-month setlist data
   */
  private async hasSufficientSetlistData(artistMbid: string): Promise<boolean> {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://setlist-playlist-generator.onrender.com');
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout for more complex validation
      
      // Use the backend endpoint that validates 6-month setlist data sufficiency
      const response = await fetch(`${apiUrl}/api/artists/validate-setlist-data/${artistMbid}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data.hasSufficientData === true;
      }
      
      // If API fails, be strict - don't show artists without confirmed setlist data
      logger.warn(`⚠️ Failed to validate setlist data for ${artistMbid}, response status: ${response.status} - rejecting for recently played`);
      return false; // Strict validation for recently played
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger.warn(`Setlist validation timeout for ${artistMbid} - rejecting for recently played`);
        return false; // Strict validation on timeout
      } else if (error instanceof Error && !error.toString().includes('404')) {
        // Only log non-404 errors to reduce console noise
        logger.warn(`Could not validate setlist data for ${artistMbid} - rejecting for recently played:`, error);
        return false; // Strict validation on errors
      } else if (typeof error === 'string' && !error.includes('404')) {
        logger.warn(`Could not validate setlist data for ${artistMbid} - rejecting for recently played:`, error);
        return false; // Strict validation on errors
      }
      return false;
    }
  }

  /**
   * Get recently played artists (fallback method)
   */
  getRecentlyPlayed(): Artist[] {
    return this.clientSearch.getRecentlyPlayed();
  }

  /**
   * Check if an artist exists in the client index
   */
  hasArtistInIndex(artistName: string): boolean {
    return this.clientSearch.hasArtist(artistName);
  }

  /**
   * Search using MusicBrainz Live Data feed with fuzzy search capabilities
   * @param query - Search query
   * @returns Promise<Artist[]> - Array of artists from MusicBrainz
   */
  private async searchMusicBrainzLive(query: string): Promise<Artist[]> {
    try {
      logger.log(`🔍 Searching MusicBrainz Live Data with fuzzy matching for: "${query}"`);
      
      // Respect rate limit
      await this.waitForMusicBrainzRateLimit();
      
      // Use MusicBrainz API directly for live search results
      const baseURL = 'https://musicbrainz.org/ws/2';
      const userAgent = 'SetlistPlaylistGenerator/1.0 (https://github.com/user/setlist-playlist-generator)';
      
      // Build intelligent fuzzy search query
      const fuzzyQuery = this.buildFuzzySearchQuery(query);
      logger.log(`🎯 Fuzzy search query: "${fuzzyQuery}"`);
      
      const searchUrl = `${baseURL}/artist?query=${encodeURIComponent(fuzzyQuery)}&fmt=json&limit=10`;
      
      const response = await fetch(searchUrl, {
        headers: {
          'User-Agent': userAgent
        }
      });

      if (!response.ok) {
        if (response.status === 503) {
          logger.warn('🚫 MusicBrainz rate limited, falling back to other sources');
          return [];
        }
        throw new Error(`MusicBrainz API error: ${response.status}`);
      }

      const data = await response.json();
      logger.log(`✅ MusicBrainz Live Data returned ${data.artists.length} results for "${query}"`);
      
      if (!data.artists || data.artists.length === 0) {
        return [];
      }

      // Transform MusicBrainz results to our Artist format
      const artists: Artist[] = data.artists
        .filter((mbArtist: any) => mbArtist.name && mbArtist.id)
        .map((mbArtist: any) => ({
          mbid: mbArtist.id,
          name: mbArtist.name,
          sortName: mbArtist['sort-name'] || mbArtist.name,
          verified: true,
          source: 'musicbrainz' as const,
          disambiguation: mbArtist.disambiguation || undefined,
          // Add additional MusicBrainz metadata
          country: mbArtist.country || undefined,
          lifeSpan: mbArtist['life-span'] ? {
            begin: mbArtist['life-span'].begin,
            end: mbArtist['life-span'].end,
            ended: mbArtist['life-span'].ended
          } : undefined,
          // Score from MusicBrainz search relevance
          searchScore: mbArtist.score || 0
        }))
        .sort((a: Artist, b: Artist) => {
          // Sort by search relevance score (higher is better)
          const scoreA = a.searchScore || 0;
          const scoreB = b.searchScore || 0;
          if (scoreA !== scoreB) {
            return scoreB - scoreA;
          }
          
          // Then by exact name match
          const queryLower = query.toLowerCase();
          const exactMatchA = a.name.toLowerCase() === queryLower ? 1 : 0;
          const exactMatchB = b.name.toLowerCase() === queryLower ? 1 : 0;
          if (exactMatchA !== exactMatchB) {
            return exactMatchB - exactMatchA;
          }
          
          // Finally by name similarity
          return a.name.localeCompare(b.name);
        })
        .slice(0, 8); // Limit to 8 results for UI

      logger.log(`🎯 Transformed ${artists.length} MusicBrainz artists for autocomplete`);
      return artists;
      
    } catch (error) {
      logger.error(`❌ MusicBrainz Live Data search failed for "${query}":`, error);
      throw error;
    }
  }

  /**
   * Build intelligent fuzzy search query for MusicBrainz with enhanced matching
   * @param query - User input query
   * @returns Optimized search query with fuzzy matching
   */
  private buildFuzzySearchQuery(query: string): string {
    const cleanQuery = query.trim().toLowerCase();
    
    // Handle short queries (1-5 characters) with aggressive wildcarding
    if (cleanQuery.length < 6) {
      const strategies = [
        `artist:"${cleanQuery}"^5`,           // Exact match (highest priority)
        `artist:${cleanQuery}*^4`,            // Prefix wildcard (matches "stereo" in "stereolab")
        `alias:"${cleanQuery}"^4`,            // Alias exact match
        `alias:${cleanQuery}*^3`,             // Alias prefix wildcard
        `artist:*${cleanQuery}*^2`,           // Contains match (any position)
        `alias:*${cleanQuery}*^1.5`,          // Alias contains match
        `sortname:${cleanQuery}*^3.5`,        // Sort name prefix match
        `artist:"${cleanQuery} *"^3`,         // Exact phrase with space (for "The Beatles", etc.)
        `artist:${cleanQuery}~0.8^2.5`        // Fuzzy match for typos
      ];
      return `(${strategies.join(' OR ')})`;
    }
    
    // Apply common artist name corrections and expansions
    const correctedQuery = this.applyKnownCorrections(cleanQuery);
    const words = correctedQuery.split(/\s+/).filter(word => word.length > 0);
    
    if (words.length === 1) {
      const word = words[0];
      
      // Enhanced single-word search with multiple fuzzy levels
      const strategies = [
        `artist:"${word}"^4`,           // Exact match (highest priority)
        `artist:${word}~0.9^3`,         // High similarity fuzzy match
        `artist:${word}~0.8^2.5`,       // Medium similarity fuzzy match
        `artist:${word}*^2`,            // Wildcard for partial matches
        `alias:"${word}"^3`,            // Alias exact match
        `alias:${word}~0.8^2`,          // Alias fuzzy match
        `alias:${word}*^1.5`,           // Alias wildcard
        // Handle common variations
        `artist:${word}s~0.9^1.5`,      // Plural forms
        `artist:the_${word}~0.8^1.5`    // "The" prefix variations
      ];
      
      return `(${strategies.join(' OR ')})`;
    } else {
      // Multi-word enhanced search
      const fullPhrase = words.join(' ');
      
      // Primary strategies for phrase matching
      const primaryStrategies = [
        `artist:"${fullPhrase}"^5`,      // Exact phrase (highest priority)
        `artist:${fullPhrase}~0.9^4`,    // High similarity phrase
        `artist:${fullPhrase}~0.8^3.5`,  // Medium similarity phrase
        `alias:"${fullPhrase}"^4`,       // Alias exact phrase
        `alias:${fullPhrase}~0.8^3`      // Alias fuzzy phrase
      ];
      
      // Secondary strategies for word combinations
      const wordCombinations = [
        // All words must appear with fuzzy matching
        words.map(word => `artist:${word}~0.8`).join(' AND '),
        // All words in aliases
        words.map(word => `alias:${word}~0.7`).join(' AND '),
        // Mix of artist and alias matches
        words.map((word, index) => 
          index % 2 === 0 ? `artist:${word}~0.8` : `alias:${word}~0.7`
        ).join(' AND ')
      ].map(combo => `(${combo})^2`);
      
      // Partial word matching for incomplete queries
      const partialMatches = [
        words.map(word => `artist:${word}*`).join(' AND '),
        words.map(word => `alias:${word}*`).join(' AND ')
      ].map(combo => `(${combo})^1.5`);
      
      const allStrategies = [
        ...primaryStrategies,
        ...wordCombinations,
        ...partialMatches
      ];
      
      return `(${allStrategies.join(' OR ')})`;
    }
  }

  /**
   * Apply known corrections and expansions for common artist name patterns
   * @param query - Original query
   * @returns Corrected query
   */
  private applyKnownCorrections(query: string): string {
    // Common misspellings and variations
    const corrections: { [key: string]: string } = {
      // Common artist name corrections
      'radiohed': 'radiohead',
      'radohead': 'radiohead',
      'radiohhead': 'radiohead',
      'artic monkeys': 'arctic monkeys',
      'taylr swift': 'taylor swift',
      'billie eillish': 'billie eilish',
      'the weekend': 'the weeknd',
      'tame impla': 'tame impala',
      
      // Common abbreviations and aliases
      'tswift': 'taylor swift',
      'am': 'arctic monkeys',
      'rhcp': 'red hot chili peppers',
      'qotsa': 'queens of the stone age',
      'soad': 'system of a down',
      
      // Partial name expansions
      'stereo': 'stereolab',
      'vampire': 'vampire weekend',
      'tame': 'tame impala',
      'glass': 'glass animals'
    };
    
    // Apply direct corrections
    if (corrections[query]) {
      logger.log(`🔧 Applied correction: "${query}" → "${corrections[query]}"`);
      return corrections[query];
    }
    
    // Apply partial corrections for multi-word queries
    const words = query.split(/\s+/);
    const correctedWords = words.map(word => corrections[word] || word);
    
    if (correctedWords.some((word, index) => word !== words[index])) {
      const corrected = correctedWords.join(' ');
      logger.log(`🔧 Applied partial correction: "${query}" → "${corrected}"`);
      return corrected;
    }
    
    return query;
  }

  /**
   * Wait for MusicBrainz rate limit to be respected
   */
  private async waitForMusicBrainzRateLimit(): Promise<void> {
    // MusicBrainz rate limiting removed
  }

  /**
   * Get cached search result if valid
   */
  private getCachedSearchResult(cacheKey: string): SearchResult | null {
    const cached = this.searchCache.get(cacheKey);
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.timestamp > this.SEARCH_CACHE_DURATION;
    if (isExpired) {
      this.searchCache.delete(cacheKey);
      return null;
    }
    
    return cached.result;
  }

  /**
   * Set cached search result with timestamp
   */
  private setCachedSearchResult(cacheKey: string, result: SearchResult): void {
    // Manage cache size
    if (this.searchCache.size >= this.MAX_CACHE_SIZE) {
      // Remove oldest entries
      const entries = Array.from(this.searchCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, Math.floor(this.MAX_CACHE_SIZE / 4));
      toRemove.forEach(([key]) => this.searchCache.delete(key));
    }
    
    this.searchCache.set(cacheKey, {
      result,
      timestamp: Date.now()
    });

    // Automatically save search cache to localStorage (debounced)
    this.debouncedSaveCache();
  }

  /**
   * Get cached setlist data if valid
   */
  private getCachedSetlistData(cacheKey: string): SetlistData | null {
    const cached = this.setlistCache.get(cacheKey);
    if (!cached) return null;
    
    const isExpired = Date.now() - cached.timestamp > this.SETLIST_CACHE_DURATION;
    if (isExpired) {
      this.setlistCache.delete(cacheKey);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Set cached setlist data with timestamp
   */
  private setCachedSetlistData(cacheKey: string, data: SetlistData): void {
    // Manage cache size
    if (this.setlistCache.size >= this.MAX_CACHE_SIZE) {
      const entries = Array.from(this.setlistCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, Math.floor(this.MAX_CACHE_SIZE / 4));
      toRemove.forEach(([key]) => this.setlistCache.delete(key));
    }
    
    this.setlistCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    // Automatically save setlist cache to localStorage (debounced)
    this.debouncedSaveCache();
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    logger.log('🧹 Clearing all caches...');
    this.searchCache.clear();
    this.setlistCache.clear();
    // MusicBrainz mapping cache cleared
    this.popularityService.clearCache();
    
    // MVP: Skip user data refresh to avoid auth errors
    // this.userDataService.forceRefresh();
    
    // Clear platform-specific caches
    const platformService = this.getCurrentMusicPlatformService();
    if (platformService && platformService.platformType === 'spotify') {
      // Clear Spotify caches if available
      try {
        const { SpotifyRecentlyPlayed } = require('./SpotifyRecentlyPlayed');
        SpotifyRecentlyPlayed.getInstance().clearCache();
      } catch (error) {
        logger.log('🧹 Could not clear Spotify cache:', error);
      }
    }
  }

  /**
   * Format duration from milliseconds to MM:SS format
   */
  private formatDuration(durationMs: number): string {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Merge results from different sources, prioritizing quality
   */
  private mergeResults(clientResults: Artist[], appleResults: Artist[]): Artist[] {
    const merged = [...clientResults];
    
    // Add Apple Music results that aren't already in client results
    appleResults.forEach(appleArtist => {
      const normalizedAppleName = appleArtist.name.toLowerCase();
      const exists = merged.some(clientArtist => 
        clientArtist.name.toLowerCase() === normalizedAppleName
      );
      
      if (!exists) {
        merged.push(appleArtist);
      }
    });

    // Sort by relevance: exact matches first, then by source preference
    return merged
      .sort((a, b) => {
        // Prioritize verified artists
        if (a.verified !== b.verified) {
          return b.verified ? 1 : -1;
        }
        
        // Prioritize client results (better MusicBrainz mapping)
        if (a.source !== b.source) {
          if (a.source === 'client') return -1;
          if (b.source === 'client') return 1;
        }
        
        return 0;
      })
      .slice(0, 8); // Limit to 8 results
  }

  /**
   * Mock setlist data generator
   */
  private getMockSetlistData(artistName: string): SetlistData {
    // This would be replaced with real setlist.fm API calls
    const mockSetlists: Record<string, SetlistData> = {
      'Taylor Swift': {
        songs: [
          { name: 'Lavender Haze', frequency: 95, playedIn: '19/20 shows', duration: '3:22' },
          { name: 'Anti-Hero', frequency: 92, playedIn: '18/20 shows', duration: '3:20' },
          { name: 'Midnight Rain', frequency: 89, playedIn: '17/20 shows', duration: '2:54' },
          { name: 'Love Story', frequency: 87, playedIn: '17/20 shows', duration: '3:55' },
          { name: 'Shake It Off', frequency: 84, playedIn: '16/20 shows', duration: '3:39' },
          { name: 'Blank Space', frequency: 82, playedIn: '16/20 shows', duration: '3:51' },
          { name: 'We Are Never Getting Back Together', frequency: 79, playedIn: '15/20 shows', duration: '3:13' },
          { name: '22', frequency: 76, playedIn: '15/20 shows', duration: '3:52' },
          { name: 'You Belong With Me', frequency: 73, playedIn: '14/20 shows', duration: '3:51' },
          { name: 'Enchanted', frequency: 68, playedIn: '13/20 shows', duration: '5:52' },
          { name: 'All Too Well (10 Minute Version)', frequency: 65, playedIn: '13/20 shows', duration: '10:13' },
          { name: 'Style', frequency: 61, playedIn: '12/20 shows', duration: '3:51' },
        ],
        context: 'Based on 20 shows from Eras Tour 2024',
        confidence: 'high'
      },
      'Arctic Monkeys': {
        songs: [
          { name: 'Do I Wanna Know?', frequency: 94, playedIn: '17/18 shows', duration: '4:32' },
          { name: 'R U Mine?', frequency: 91, playedIn: '16/18 shows', duration: '3:21' },
          { name: 'I Bet You Look Good on the Dancefloor', frequency: 89, playedIn: '16/18 shows', duration: '2:53' },
          { name: 'Fluorescent Adolescent', frequency: 86, playedIn: '15/18 shows', duration: '2:57' },
          { name: 'When the Sun Goes Down', frequency: 83, playedIn: '15/18 shows', duration: '3:20' },
          { name: 'Knee Socks', frequency: 78, playedIn: '14/18 shows', duration: '4:17' },
          { name: '505', frequency: 75, playedIn: '13/18 shows', duration: '4:13' },
          { name: 'Arabella', frequency: 72, playedIn: '13/18 shows', duration: '3:27' },
          { name: 'The View from the Afternoon', frequency: 67, playedIn: '12/18 shows', duration: '3:38' },
          { name: 'Mardy Bum', frequency: 64, playedIn: '11/18 shows', duration: '2:55' },
        ],
        context: 'Based on 18 shows from 2023 tour',
        confidence: 'high'
      },
      'Radiohead': {
        songs: [
          { name: 'Creep', frequency: 88, playedIn: '15/17 shows', duration: '3:58' },
          { name: 'Paranoid Android', frequency: 85, playedIn: '14/17 shows', duration: '6:23' },
          { name: 'Karma Police', frequency: 82, playedIn: '14/17 shows', duration: '4:21' },
          { name: 'No Surprises', frequency: 79, playedIn: '13/17 shows', duration: '3:48' },
          { name: 'Everything In Its Right Place', frequency: 76, playedIn: '13/17 shows', duration: '4:11' },
          { name: 'Fake Plastic Trees', frequency: 71, playedIn: '12/17 shows', duration: '4:50' },
          { name: 'High and Dry', frequency: 68, playedIn: '11/17 shows', duration: '4:17' },
          { name: '15 Step', frequency: 65, playedIn: '11/17 shows', duration: '3:57' },
          { name: 'Street Spirit (Fade Out)', frequency: 62, playedIn: '10/17 shows', duration: '4:12' },
          { name: 'Lucky', frequency: 59, playedIn: '10/17 shows', duration: '4:19' },
        ],
        context: 'Based on 17 shows from recent tours (2022-2023)',
        confidence: 'medium'
      },
      'Pixies': {
        songs: [
          { name: 'Debaser', frequency: 95, playedIn: '19/20 shows', duration: '2:52' },
          { name: 'Wave of Mutilation', frequency: 90, playedIn: '18/20 shows', duration: '3:04' },
          { name: 'Here Comes Your Man', frequency: 88, playedIn: '17/20 shows', duration: '3:21' },
          { name: 'Monkey Gone to Heaven', frequency: 85, playedIn: '17/20 shows', duration: '2:57' },
          { name: 'Gigantic', frequency: 82, playedIn: '16/20 shows', duration: '3:06' },
          { name: 'Where Is My Mind?', frequency: 80, playedIn: '16/20 shows', duration: '3:53' },
          { name: 'Hey', frequency: 75, playedIn: '15/20 shows', duration: '3:31' },
          { name: 'Bone Machine', frequency: 70, playedIn: '14/20 shows', duration: '3:02' },
        ],
        context: 'Based on 20 recent Pixies shows',
        confidence: 'high'
      },
    };

    // Return specific data if available, otherwise return empty with error message
    return mockSetlists[artistName] || {
      songs: [
        { name: `No setlist data available for ${artistName}`, frequency: 0, playedIn: 'Temporarily unavailable', duration: '0:00' }
      ],
      context: `Based on recent ${artistName} shows`,
      confidence: 'medium'
    };
  }

  /**
   * Get item from artist setlist cache
   * @param key - Cache key
   * @returns Cached result or null if not found/expired
   */
  private getFromCache(key: string): Artist | null {
    const cached = this.artistSetlistCache.get(key);
    if (cached && Date.now() < cached.timestamp) {
      return cached.result;
    }
    if (cached) {
      this.artistSetlistCache.delete(key); // Remove expired entries
    }
    return null;
  }

  /**
   * Set item in artist setlist cache
   * @param key - Cache key
   * @param result - Artist result (or null for negative cache)
   * @param duration - Cache duration in milliseconds
   */
  private setInCache(key: string, result: Artist | null, duration: number): void {
    this.artistSetlistCache.set(key, {
      result,
      timestamp: Date.now() + duration
    });
    
    // Clean up cache if it gets too large
    if (this.artistSetlistCache.size > 100) {
      const oldestKey = this.artistSetlistCache.keys().next().value;
      this.artistSetlistCache.delete(oldestKey);
    }
  }

  /**
   * Performance-first search that prioritizes user's listening history and known touring artists
   * @param query - Search query
   * @param searchId - Search tracking ID for logging
   * @returns Promise<SearchResult> - Search results optimized for speed
   */
  private async performanceFirstSearch(query: string, searchId: string): Promise<SearchResult> {
    const searchStart = performance.now();
    const timings: Record<string, number> = {};
    
    // Step 1: Search user's listening history first (fastest)
    const userStart = performance.now();
    const userResults = await this.searchUserListeningHistory(query, searchId);
    timings['user-history'] = performance.now() - userStart;
    
    // Step 2: Search known touring artists (cached, very fast)
    const touringStart = performance.now();
    const touringResults = await this.searchKnownTouringArtists(query, searchId);
    timings['known-touring'] = performance.now() - touringStart;
    
    // Step 3: Quick platform search with pre-filtering (if needed for more results)
    let platformResults: Artist[] = [];
    const totalFound = userResults.length + touringResults.length;
    
    if (totalFound < 5) {
      const platformStart = performance.now();
      platformResults = await this.quickPlatformSearch(query, 5 - totalFound, searchId);
      timings['platform-quick'] = performance.now() - platformStart;
    }
    
    // Combine and deduplicate results
    const allResults = [...userResults, ...touringResults, ...platformResults];
    const deduplicatedResults = this.deduplicateArtists(allResults);
    
    // Apply fast relevance scoring with query relevance
    const scoredResults = this.sortArtistsByFastRelevance(deduplicatedResults, query);
    
    const totalTime = performance.now() - searchStart;
    timings['total'] = totalTime;
    
    searchLogger.performance(searchId, timings);
    
    return {
      artists: scoredResults.slice(0, 10), // Limit to top 10 for performance
      source: 'hybrid',
      hasMore: scoredResults.length > 10
    };
  }

  /**
   * Search user's listening history for matching artists (very fast, no API calls)
   * @param query - Search query
   * @param searchId - Search tracking ID for logging
   * @returns Promise<Artist[]> - Matching artists from user's listening data
   */
  private async searchUserListeningHistory(query: string, searchId: string): Promise<Artist[]> {
    try {
      searchLogger.userData(searchId, 'fetch');
      const normalizedQuery = query.toLowerCase().trim();
      const queryWords = normalizedQuery.split(/\s+/);
      
      // Get user's recently played and library artists (cached)
      const userProfile = await this.userDataService.getUserMusicProfile();
      const allUserArtists = [
        ...Array.from(userProfile.recentlyPlayedArtists),
        ...Array.from(userProfile.libraryArtists)
      ];
      
      // Filter for matching artists
      const matchingArtists: Artist[] = [];
      
      for (const artistName of allUserArtists) {
        const normalizedArtist = artistName.toLowerCase();
        
        // Check for exact match or partial match
        const isMatch = queryWords.some(word => 
          normalizedArtist.includes(word) || 
          word.includes(normalizedArtist) ||
          this.calculateSimilarity(normalizedArtist, word) > 0.7
        );
        
        if (isMatch) {
          // Check cache for this artist's validation status
          const cacheKey = `artist_setlist_${artistName.toLowerCase()}`;
          const cachedArtist = this.getFromCache(cacheKey);
          
          if (cachedArtist) {
            matchingArtists.push(cachedArtist);
          } else {
            // Create basic artist object (will be validated if selected)
            matchingArtists.push({
              mbid: '', // Will be populated during validation
              name: artistName,
              sortName: artistName,
              verified: false,
              source: 'discovery'
            });
          }
        }
      }
      
      const results = matchingArtists.slice(0, 3);
      searchLogger.userData(searchId, 'match', { count: results.length });
      return results;
      
    } catch (error) {
      searchLogger.error(searchId, 'USER_DATA', 'User history search failed', error);
      return [];
    }
  }

  /**
   * Search known touring artists from pre-validated cache (very fast)
   * @param query - Search query
   * @param searchId - Search tracking ID for logging
   * @returns Promise<Artist[]> - Matching known touring artists
   */
  private async searchKnownTouringArtists(query: string, searchId: string): Promise<Artist[]> {
    try {
      const normalizedQuery = query.toLowerCase().trim();
      const queryWords = normalizedQuery.split(/\s+/);
      
      // Get fallback popular artists (these are known to have recent setlist data)
      const popularArtists = await this.getFallbackPopularArtists();
      
      // Filter for matching artists
      const matchingTouringArtists: Artist[] = [];
      
      for (const artist of popularArtists) {
        const normalizedArtist = artist.name.toLowerCase();
        
        // Check for match
        const isMatch = queryWords.some(word => 
          normalizedArtist.includes(word) || 
          word.includes(normalizedArtist) ||
          this.calculateSimilarity(normalizedArtist, word) > 0.7
        );
        
        if (isMatch) {
          matchingTouringArtists.push({
            ...artist,
            verified: true, // These are pre-validated
            source: 'recommendation'
          });
        }
      }
      
      const results = matchingTouringArtists.slice(0, 3);
      if (results.length > 0) {
        searchLogger.cache(searchId, 'hit', `Found ${results.length} known touring artists`);
      }
      return results;
      
    } catch (error) {
      searchLogger.error(searchId, 'SEARCH', 'Known touring artists search failed', error);
      return [];
    }
  }

  /**
   * Quick platform search with minimal processing (when more results needed)
   * @param query - Search query
   * @param maxResults - Maximum results needed
   * @param searchId - Search tracking ID for logging
   * @returns Promise<Artist[]> - Platform search results
   */
  private async quickPlatformSearch(query: string, maxResults: number, searchId: string): Promise<Artist[]> {
    try {
      const platformService = this.getCurrentMusicPlatformService();
      if (!platformService) {
        return [];
      }
      
      searchLogger.api(searchId, platformService.platformType, 'start');
      const results = await platformService.searchArtists(query);
      
      // Return limited results for performance - transform to required format
      const transformedResults = results.slice(0, maxResults).map(artist => ({
        mbid: artist.mbid || '', // Ensure mbid is not undefined
        name: artist.name,
        sortName: artist.sortName || artist.name,
        verified: artist.verified || false,
        source: 'discovery' as const,
        artwork: artist.artwork,
        externalUrl: artist.externalUrl,
        platform: artist.platform,
        followers: artist.followers,
        genres: artist.genres
      }));
      
      searchLogger.api(searchId, platformService.platformType, 'success', { count: transformedResults.length });
      return transformedResults;
      
    } catch (error) {
      searchLogger.api(searchId, 'platform', 'error', { error: error instanceof Error ? error.message : 'Unknown error' });
      return [];
    }
  }

  /**
   * Deduplicate artists by name and MBID
   * @param artists - Array of artists to deduplicate
   * @returns Artist[] - Deduplicated artists
   */
  private deduplicateArtists(artists: Artist[]): Artist[] {
    const seen = new Set<string>();
    const deduplicatedArtists: Artist[] = [];
    
    for (const artist of artists) {
      const key = `${artist.mbid || 'no-mbid'}_${artist.name.toLowerCase()}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        deduplicatedArtists.push(artist);
      }
    }
    
    return deduplicatedArtists;
  }

  /**
   * Validate that cached search results are relevant to the query to prevent wrong artists
   * @param cachedResult - The cached search result to validate
   * @param normalizedQuery - The normalized query string
   * @returns boolean - True if the cached result is valid for this query
   */
  private validateCachedResult(cachedResult: SearchResult, normalizedQuery: string): boolean {
    try {
      // If no artists in cache, it's valid (empty result)
      if (!cachedResult.artists || cachedResult.artists.length === 0) {
        return true;
      }

      // Check if any of the top 3 results are relevant to the query
      const topResults = cachedResult.artists.slice(0, 3);
      const queryWords = normalizedQuery.split(/\s+/).filter(word => word.length > 0);
      
      let relevantCount = 0;
      for (const artist of topResults) {
        const artistName = artist.name.toLowerCase();
        
        // Check if artist name contains any query words
        const hasRelevantWords = queryWords.some(word => {
          return artistName.includes(word) || 
                 word.includes(artistName) ||
                 this.calculateSimilarity(artistName, word) > 0.6;
        });
        
        if (hasRelevantWords) {
          relevantCount++;
        }
      }
      
      // At least one of the top 3 results should be relevant
      const isValid = relevantCount > 0;
      
      if (!isValid) {
        logger.log(`🚫 Cache validation failed: query "${normalizedQuery}" vs top results: ${topResults.map(a => a.name).join(', ')}`);
      }
      
      return isValid;
      
    } catch (error) {
      logger.warn('⚠️ Cache validation error:', error);
      // If validation fails, invalidate the cache for safety
      return false;
    }
  }

  /**
   * Calculate string similarity using Levenshtein distance
   * @param str1 - First string
   * @param str2 - Second string
   * @returns number - Similarity score (0-1)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   * @param str1 - First string
   * @param str2 - Second string
   * @returns number - Edit distance
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const substitutionCost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,       // insertion
          matrix[j - 1][i] + 1,       // deletion
          matrix[j - 1][i - 1] + substitutionCost // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }
}